import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DatePicker, DateTimePicker } from '@material-ui/pickers';
import { DateTime } from 'luxon';

// stuff for dialog - move if separating
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Collapse from '@material-ui/core/Collapse';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import Divider from '@material-ui/core/Divider';
import Switch from '@material-ui/core/Switch';
import CircularProgress from '@material-ui/core/CircularProgress';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';

import PaginatedTable from 'app/components/tables/PaginatedTable';
import EmailCampaignsDialog from 'app/components/accounting/invoices/EmailCampaignsDialog';
import BatchPaymentDialog from 'app/components/accounting/payments/BatchPaymentDialog';
import BatchRefundDialog from 'app/components/accounting/refunds/BatchRefundDialog';

import { containerFull } from 'app/styles/new-layout';

import useApi from 'app/context/useApi';

const invoiceTypes = [
    'Dues',
    'Ticket',
    'Standard',
]

const statuses = [
    'Paid',
    'Partially Paid',
    'Outstanding',
    'Past Due',
    'Refunded',
    'Partially Refunded',
    //'Refund Pending',
]

const dateRange = [
    'Before',
    'After',
    'Between',
    'Outside',
    'Year'
]

const operators = [
    'Greater Than',
    'Less Than',
    'Equal To',
    'Not Equal To'
]

const dualDates = [ "Between", "Outside" ];

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        className={props.className}
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        {...props.inputProps}
    />
);

const useListItemStyles = makeStyles(theme => ({
    nested: props => ({
        paddingLeft: theme.spacing((props.depth * 2)),
    }),
    listItemIcon: {
        minWidth: 0
    }
}));

const findChildIds = (children) => {

    let ids = [];

    children.forEach(c => {

        ids.push(c.id);

        if (c.children_with_count.length > 0) {
            ids = ids.concat(findChildIds(c.children_with_count));
        }
    })

    return ids;
}

const NestedLevelItem = (props) => {

    const { level, depth, levelsChecked, onCheck, onCheckChildren } = props;

    const classes = useListItemStyles({ depth: depth});

    const [ includeChildren, setIncludeChildren ] = useState(false);

    const isSelected = levelsChecked.indexOf(level.id) > -1 ;

    const childIds = (level.children_with_count.length === 0) ? 0 : useMemo(() => findChildIds(level.children_with_count), []);

    const handleIncludeChildren = () => {
        setIncludeChildren(! includeChildren);
        const ids = [...childIds];
        if (! includeChildren) ids.push(level.id);
        onCheckChildren(! includeChildren, ids);
    }

    return (
        <>
            <ListItem
                classes={{ root: classes.nested }}
                button
                dense
                onClick={() => onCheck(level.id)}
                >
                <ListItemIcon classes={{ root: classes.listItemIcon }}>
                    <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        color="primary"
                    />
                </ListItemIcon>
                <ListItemText
                    primary={ level.title }
                    className={ classes.listItemText }
                />
                <ListItemSecondaryAction>
                    <Typography
                        display="inline"
                        color="textSecondary"
                        variant="caption"
                        >
                        Incl. Sub
                    </Typography>
                    <Switch
                        edge="end"
                        onChange={handleIncludeChildren}
                        checked={includeChildren}
                        disabled={level.children_with_count.length === 0}
                        color="primary"
                    />
                </ListItemSecondaryAction>
            </ListItem>
            {level.children_with_count.map((child) => (
                <NestedLevelItem
                    key={child.id}
                    level={child}
                    depth={(depth + 1)}
                    levelsChecked={levelsChecked}
                    onCheck={onCheck}
                    onCheckChildren={onCheckChildren}
                />
            ))}
        </>
    )
}

const useFilterStyles = makeStyles(theme => ({
    section: {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    subSection: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.grey[200]
    },
    subSectionList: {
        backgroundColor: theme.palette.grey[200],
        padding: 0
    },
    subSectionLoading: {
        textAlign: 'center',
        padding: theme.spacing(3),
        backgroundColor: theme.palette.grey[200]
    },
    dialogSpacing: {
        padding: theme.spacing(1)
    },
    labelPadding: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1)
    },
    noMarginLeft: {
        marginLeft: 0
    },
    empty: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    entered: {
        overflowY: 'scroll'
    }
}));

const InvoiceFilterDialog = (props) => {

    const classes = useFilterStyles();

    const { open, onClose, onFilter } = props;

    const api = useApi()

    const [ filters, setFilters ] = useState([]);

    const [ filtersChecked, setFiltersChecked ] = useState([]);
    const [ typesChecked, setTypesChecked ] = useState([]);
    const [ levelsChecked, setLevelsChecked ] = useState([]);
    const [ groupsChecked, setGroupsChecked ] = useState([]);

    const [ invoiceAmount, setInvoiceAmount ] = useState(0);
    const [ invoiceAmountOperator, setInvoiceAmountOperator ] = useState("Greater Than");

    const [ invoiceAmountDue, setInvoiceAmountDue ] = useState(0);
    const [ invoiceAmountDueOperator, setInvoiceAmountDueOperator ] = useState("Greater Than");

    const [ invoiceAmountPaid, setInvoiceAmountPaid ] = useState(0);
    const [ invoiceAmountPaidOperator, setInvoiceAmountPaidOperator ] = useState("Greater Than");

    const [ dueByDate, setDueByDate ] = useState(DateTime.local().startOf('day'));
    const [ dueByDate2, setDueByDate2 ] = useState(null);
    const [ dueByRange, setDueByRange ] = useState("Before");

    const [ invoiceDate, setInvoiceDate ] = useState(DateTime.local().startOf('day'));
    const [ invoiceDate2, setInvoiceDate2 ] = useState(null);
    const [ invoiceRange, setInvoiceRange ] = useState("Before");

    const [ paidOnDate, setPaidOnDate ] = useState(DateTime.local().startOf('day'));
    const [ paidOnDate2, setPaidOnDate2 ] = useState(null);
    const [ paidOnRange, setPaidOnRange ] = useState("Before");

    const [ createdAtDate, setCreatedAtDate ] = useState(DateTime.local().startOf('day'));
    const [ createdAtDate2, setCreatedAtDate2 ] = useState(null);
    const [ createdAtRange, setCreatedAtRange ] = useState("Before");

    const [ groups, setGroups ] = useState([]);
    const [ levels, setLevels ] = useState([]);

    const [ groupsLoading, setGroupsLoading ] = useState(false);
    const [ levelsLoading, setLevelsLoading ] = useState(false);

    // Getters --------------------------------------

    const getGroups = () => {
        setGroupsLoading(true);

        api.get('/admin/group/group')
            .then(res => setGroups(res.groups))
            .finally(() => setGroupsLoading(false));
    }

    const getLevels = () => {
        setLevelsLoading(true);

        api.get('/admin/group/level/recursive/0')
            .then(res => setLevels(res.groups))
            .finally(() => setLevelsLoading(false));
    }

    useEffect(() => {
        getGroups();
        getLevels();
    }, []);

    // Effects --------------------------------------

    useEffect(() => {
        setDueByDate2(dueByDate.plus({days: 15}));
    }, [dueByDate]);

    useEffect(() => {
        setInvoiceDate2(invoiceDate.plus({days: 15}));
    }, [invoiceDate]);

    useEffect(() => {
        setPaidOnDate2(paidOnDate.plus({days: 15}));
    }, [paidOnDate]);

    useEffect(() => {
        setCreatedAtDate2(createdAtDate.plus({days: 15}));
    }, [createdAtDate]);

    // Handlers --------------------------------------

    const handleFilter = filter => {
        const newFilters = [...filters];
        const i = newFilters.indexOf(filter)

        if (i > -1) {
            newFilters.splice(i, 1)
        } else {
            newFilters.push(filter)
        }

        setFilters(newFilters);
    }

    const handleGroup = id => {
        const newChecked = [...groupsChecked]
        const i = newChecked.indexOf(id)

        if (i > -1) {
            newChecked.splice(i, 1)
        } else {
            newChecked.push(id)
        }

        setGroupsChecked(newChecked);
    }

    const handleLevel = id => {
        const newChecked = [...levelsChecked]
        const i = newChecked.indexOf(id)

        if (i > -1) {
            newChecked.splice(i, 1)
        } else {
            newChecked.push(id)
        }

        setLevelsChecked(newChecked);
    }

    const handleLevelChildren = (addChildren, ids) => {
        let newChecked = [...levelsChecked]

        if (addChildren) {
            newChecked = [...new Set(newChecked.concat(ids))];
        } else {
            newChecked = newChecked.filter((el) => !ids.includes(el));
        }

        setLevelsChecked(newChecked);
    }

    const handleChecked = name => {

        const newChecked = [...filtersChecked];

        const i = newChecked.indexOf(name);

        if (i > -1) {
            newChecked.splice(i, 1);
        } else {
            newChecked.push(name);
        }

        setFiltersChecked(newChecked);
    }

    const handleType = name => {

        const newChecked = [...typesChecked];

        const i = newChecked.indexOf(name);

        if (i > -1) {
            newChecked.splice(i, 1);
        } else {
            newChecked.push(name);
        }

        setTypesChecked(newChecked);
    }

    const handleClose = () => {
        onClose();
    }

    // Posters -----------------------------

    const postFilter = () => {

        const data = {};

        if (filters.indexOf('unpaid_membership_dues') > -1) {
            data.unpaid_membership_dues = true;
        }

        if (filters.indexOf('invoice_type') > -1) {
            data.invoice_type = typesChecked;
        }

        if (filters.indexOf('status') > -1) {
            data.status = filtersChecked;
        }

        if (filters.indexOf('invoice_amount') > -1) {
            data.invoice_amount = {
                amount: invoiceAmount,
                operator: invoiceAmountOperator
            }
        }

        if (filters.indexOf('invoice_amount_due') > -1) {
            data.invoice_amount_due = {
                amount: invoiceAmountDue,
                operator: invoiceAmountDueOperator
            }
        }

        if (filters.indexOf('invoice_amount_paid') > -1) {
            data.invoice_amount_paid = {
                amount: invoiceAmountPaid,
                operator: invoiceAmountPaidOperator
            }
        }

        if (filters.indexOf('due_by') > -1) {
            data.due_by = {
                date_range: dueByRange,
                start_date: dueByDate.toSeconds(),
                end_date: dueByDate2.toSeconds()
            };
        }

        if (filters.indexOf('invoice_date') > -1) {
            data.invoice_date = {
                date_range: invoiceRange,
                start_date: invoiceDate.toSeconds(),
                end_date: invoiceDate2.toSeconds()
            };
        }

        if (filters.indexOf('paid_on') > -1) {
            data.paid_on = {
                date_range: paidOnRange,
                start_date: paidOnDate.toSeconds(),
                end_date: paidOnDate2.toSeconds()
            };
        }

        if (filters.indexOf('created_at') > -1) {
            data.created_at = {
                date_range: createdAtRange,
                start_date: createdAtDate.toSeconds(),
                end_date: createdAtDate2.toSeconds()
            };
        }

        if (filters.indexOf('groups') > -1 && groupsChecked.length > 0) {
            data.groups = groupsChecked;
        }

        if (filters.indexOf('membership_levels') > -1 && levelsChecked.length > 0) {
            data.levels = levelsChecked;
        }

        onFilter(data);
        onClose();
    }

    const postClear = () => {
        setFilters([]);
        setFiltersChecked([]);
        setTypesChecked([]);
        onFilter({})
        onClose();
    }

    return (
        <Dialog
            maxWidth="xs"
            open={open}
            onClose={handleClose}
            fullWidth
            aria-labelledby="filter-users"
            >
            <DialogTitle className={classes.dialogSpacing}>
                <Grid container alignItems="center" justify="space-between">
                    <Grid item>
                        <Button variant="outlined" color="primary" onClick={postClear}>
                            Clear
                        </Button>
                    </Grid>
                    <Grid item>
                        Filters
                    </Grid>
                    <Grid item>
                        <Button color="primary" variant="contained" onClick={postFilter}>
                            Filter
                        </Button>
                    </Grid>
                </Grid>
            </DialogTitle>
            <Divider />
            <FormGroup className={classes.section}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={filters.indexOf('unpaid_membership_dues') > -1}
                            onChange={() => handleFilter('unpaid_membership_dues')}
                            name="unpaid-membership-dues"
                            color="primary"
                        />
                    }
                    classes={{ label: classes.labelPadding }}
                    label="Unpaid Membership Dues"
                />
            </FormGroup>
            <Divider />
            <FormGroup className={classes.section}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={filters.indexOf('invoice_type') > -1}
                            onChange={() => handleFilter('invoice_type')}
                            name="invoice-type"
                            color="primary"
                        />
                    }
                    label="Invoice Type"
                />
            </FormGroup>
            <Divider />
            <Collapse in={filters.indexOf('invoice_type') > -1} classes={{entered: classes.entered}}>
                <FormGroup className={classes.subSection} >
                    {invoiceTypes.map(type => (
                        <FormControlLabel
                            className={classes.noMarginLeft}
                            key={type}
                            control={
                                <Checkbox
                                    checked={(typesChecked.indexOf(type) > -1)}
                                    onChange={() => handleType(type)}
                                    name={type}
                                    color="primary"
                                />
                            }
                            label={type}
                        />
                    ))}
                </FormGroup>
            </Collapse>
            <FormGroup className={classes.section}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={filters.indexOf('status') > -1}
                            onChange={() => handleFilter('status')}
                            name="payment-status"
                            color="primary"
                        />
                    }
                    label="Payment Status"
                />
            </FormGroup>
            <Divider />
            <Collapse in={filters.indexOf('status') > -1} classes={{entered: classes.entered}}>
                <FormGroup className={classes.subSection} >
                    {statuses.map(status => (
                        <FormControlLabel
                            className={classes.noMarginLeft}
                            key={status}
                            control={
                                <Checkbox
                                    checked={(filtersChecked.indexOf(status) > -1)}
                                    onChange={()=>handleChecked(status)}
                                    name={status}
                                    color="primary"
                                />
                            }
                            label={status}
                        />
                    ))}
                </FormGroup>
            </Collapse>
            <FormGroup className={classes.section}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={filters.indexOf('groups') > -1}
                            onChange={() => handleFilter('groups')}
                            name="groups"
                            color="primary"
                        />
                    }
                    label="Groups"
                />
            </FormGroup>
            <Divider />
            <Collapse in={filters.indexOf('groups') > -1} classes={{entered: classes.entered}}>
                {groupsLoading ? (
                    <div className={classes.subSectionLoading}>
                        <CircularProgress
                            color="primary"
                            size={32}
                            thickness={2}
                        />
                    </div>
                ) : groups.length ? (
                    <FormGroup className={classes.subSectionList}>
                        {groups && groups.map(group => (
                            <FormControlLabel
                                className={classes.noMarginLeft}
                                key={group.id}
                                control={
                                    <Checkbox
                                        checked={(groupsChecked.indexOf(group.id) > -1)}
                                        onChange={()=>handleGroup(group.id)}
                                        name={group.title}
                                        color="primary"
                                    />
                                }
                                label={group.title}
                            />
                        ))}
                    </FormGroup>
                ) : (
                    <div className={classes.subSection}>
                        <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            className={classes.empty}
                            align="center"
                            >
                            No Available Groups
                        </Typography>
                    </div>
                )}
            </Collapse>
            <FormGroup className={classes.section}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={filters.indexOf('membership_levels') > -1}
                            onChange={() => handleFilter('membership_levels')}
                            name="membership_levels"
                            color="primary"
                        />
                    }
                    label="Membership Levels"
                />
            </FormGroup>
            <Divider />
            <Collapse in={filters.indexOf('membership_levels') > -1} classes={{entered: classes.entered}}>
                {levelsLoading ? (
                    <div className={classes.subSectionLoading}>
                        <CircularProgress
                            color="primary"
                            size={35}
                            thickness={1.8}
                        />
                    </div>
                ) : levels.length ? (
                    <List className={classes.subSectionList}>
                        {levels.map(level => (
                            <NestedLevelItem
                                key={level.id}
                                level={level}
                                depth={1}
                                levelsChecked={levelsChecked}
                                onCheck={handleLevel}
                                onCheckChildren={handleLevelChildren}
                            />
                        ))}
                    </List>
                ) : (
                    <div className={classes.subSection}>
                        <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            className={classes.empty}
                            align="center"
                            >
                            No Available Levels
                        </Typography>
                    </div>
                )}
            </Collapse>
            <FormGroup className={classes.section}>
                <FormControlLabel
                    control={
                        <Checkbox
                        checked={filters.indexOf('invoice_amount') > -1}
                        onChange={() => handleFilter('invoice_amount')}
                            name="invoice-amount"
                            color="primary"
                        />
                    }
                    label="Invoice Amount"
                />
            </FormGroup>
            <Divider />
            <Collapse in={filters.indexOf('invoice_amount') > -1} classes={{entered: classes.entered}}>
                <div className={classes.subSection}>
                    <TextField
                        select
                        variant="outlined"
                        fullWidth
                        onChange={(e) => setInvoiceAmountOperator(e.target.value)}
                        value={invoiceAmountOperator}
                        label="Operator"
                        margin="normal"
                        >
                        {operators.map((op, index) => (
                            <MenuItem key={index} value={op}>
                                {op}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Amount"
                        type="number"
                        step={0.01}
                        value={invoiceAmount}
                        onChange={e => setInvoiceAmount(e.target.value)}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                        margin="normal"
                    />
                </div>
            </Collapse>
            <FormGroup className={classes.section}>
                <FormControlLabel
                    control={
                        <Checkbox
                        checked={filters.indexOf('invoice_amount_due') > -1}
                        onChange={() => handleFilter('invoice_amount_due')}
                            name="invoice-amount-due"
                            color="primary"
                        />
                    }
                    label="Invoice Amount Due"
                />
            </FormGroup>
            <Divider />
            <Collapse in={filters.indexOf('invoice_amount_due') > -1} classes={{entered: classes.entered}}>
                <div className={classes.subSection}>
                    <TextField
                        select
                        variant="outlined"
                        fullWidth
                        onChange={(e) => setInvoiceAmountDueOperator(e.target.value)}
                        value={invoiceAmountDueOperator}
                        label="Operator"
                        margin="normal"
                        >
                        {operators.map((op, index) => (
                            <MenuItem key={index} value={op}>
                                {op}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Amount Due"
                        type="number"
                        step={0.01}
                        value={invoiceAmountDue}
                        onChange={e => setInvoiceAmountDue(e.target.value)}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                        margin="normal"
                    />
                </div>
            </Collapse>
            <FormGroup className={classes.section}>
                <FormControlLabel
                    control={
                        <Checkbox
                        checked={filters.indexOf('invoice_amount_paid') > -1}
                        onChange={() => handleFilter('invoice_amount_paid')}
                            name="invoice-amount-paid"
                            color="primary"
                        />
                    }
                    label="Invoice Amount Paid"
                />
            </FormGroup>
            <Divider />
            <Collapse in={filters.indexOf('invoice_amount_paid') > -1} classes={{entered: classes.entered}}>
                <div className={classes.subSection}>
                    <TextField
                        select
                        variant="outlined"
                        fullWidth
                        onChange={(e) => setInvoiceAmountPaidOperator(e.target.value)}
                        value={invoiceAmountPaidOperator}
                        label="Operator"
                        margin="normal"
                        >
                        {operators.map((op, index) => (
                            <MenuItem key={index} value={op}>
                                {op}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Amount Paid"
                        type="number"
                        step={0.01}
                        value={invoiceAmountPaid}
                        onChange={e => setInvoiceAmountPaid(e.target.value)}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                        margin="normal"
                    />
                </div>
            </Collapse>
            <FormGroup className={classes.section}>
                <FormControlLabel
                    control={
                        <Checkbox
                        checked={filters.indexOf('due_by') > -1}
                        onChange={() => handleFilter('due_by')}
                            name="due-by"
                            color="primary"
                        />
                    }
                    label="Due By Date"
                />
            </FormGroup>
            <Divider />
            <Collapse in={filters.indexOf('due_by') > -1} classes={{entered: classes.entered}}>
                <div className={classes.subSection}>
                    <TextField
                        select
                        variant="outlined"
                        fullWidth
                        onChange={(e) => setDueByRange(e.target.value)}
                        value={dueByRange}
                        label="Date Range Type"
                        margin="normal"
                        >
                        {dateRange.map((range, index) => (
                            <MenuItem key={index} value={range}>
                                {range}
                            </MenuItem>
                        ))}
                    </TextField>
                    {dueByRange === 'Year' ? (
                        <DatePicker
                            disableFuture
                            openTo="year"
                            format="yyyy"
                            label="Select Year"
                            views={["year"]}
                            value={dueByDate}
                            onChange={(value) => setDueByDate(value)}
                            TextFieldComponent={renderDateInput}
                            margin="normal"
                        />
                    ) : (
                        <DateTimePicker
                            label={dualDates.includes(dueByRange) ? "Choose Start Date" : "Choose Date"}
                            inputVariant="outlined"
                            value={dueByDate}
                            onChange={(value) => setDueByDate(value)}
                            variant="outlined"
                            TextFieldComponent={renderDateInput}
                            minutesStep={15}
                            margin="normal"
                        />
                    )}

                    <Collapse in={dualDates.includes(dueByRange)}>
                        <DateTimePicker
                            label="Choose End Date"
                            inputVariant="outlined"
                            value={dueByDate2}
                            onChange={(value) => setDueByDate2(value)}
                            variant="outlined"
                            TextFieldComponent={renderDateInput}
                            minutesStep={15}
                            margin="normal"
                        />
                    </Collapse>
                </div>
            </Collapse>
            <FormGroup className={classes.section}>
                <FormControlLabel
                    control={
                        <Checkbox
                        checked={filters.indexOf('invoice_date') > -1}
                        onChange={() => handleFilter('invoice_date')}
                            name="invoice-date"
                            color="primary"
                        />
                    }
                    label="Invoice Date"
                />
            </FormGroup>
            <Divider />
            <Collapse in={filters.indexOf('invoice_date') > -1} classes={{entered: classes.entered}}>
                <div className={classes.subSection}>
                    <TextField
                        select
                        variant="outlined"
                        fullWidth
                        onChange={(e) => setInvoiceRange(e.target.value)}
                        value={invoiceRange}
                        label="Date Range Type"
                        margin="normal"
                        >
                        {dateRange.map((range, index) => (
                            <MenuItem key={index} value={range}>
                                {range}
                            </MenuItem>
                        ))}
                    </TextField>
                    {invoiceRange === 'Year' ? (
                        <DatePicker
                            disableFuture
                            openTo="year"
                            format="yyyy"
                            label="Select Year"
                            views={["year"]}
                            value={invoiceDate}
                            onChange={(value) => setInvoiceDate(value)}
                            TextFieldComponent={renderDateInput}
                            margin="normal"
                        />
                    ) : (
                        <DateTimePicker
                            label={dualDates.includes(invoiceRange) ? "Choose Start Date" : "Choose Date"}
                            inputVariant="outlined"
                            value={invoiceDate}
                            onChange={(value) => setInvoiceDate(value)}
                            variant="outlined"
                            TextFieldComponent={renderDateInput}
                            minutesStep={15}
                            margin="normal"
                        />
                    )}
                    <Collapse in={dualDates.includes(invoiceRange)}>
                        <DateTimePicker
                            label="Choose End Date"
                            inputVariant="outlined"
                            value={invoiceDate2}
                            onChange={(value) => setInvoiceDate2(value)}
                            variant="outlined"
                            TextFieldComponent={renderDateInput}
                            minutesStep={15}
                            margin="normal"
                        />
                    </Collapse>
                </div>
            </Collapse>
            <FormGroup className={classes.section}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={filters.indexOf('paid_on') > -1}
                            onChange={() => handleFilter('paid_on')}
                            name="paid-on"
                            color="primary"
                        />
                    }
                    label="Paid On Date"
                />
            </FormGroup>
            <Divider />
            <Collapse in={filters.indexOf('paid_on') > -1} classes={{entered: classes.entered}}>
                <div className={classes.subSection}>
                    <TextField
                        select
                        variant="outlined"
                        fullWidth
                        onChange={(e) => setPaidOnRange(e.target.value)}
                        value={paidOnRange}
                        label="Date Range Type"
                        margin="normal"
                        >
                        {dateRange.map((range, index) => (
                            <MenuItem key={index} value={range} >
                                {range}
                            </MenuItem>
                        ))}
                    </TextField>
                    {paidOnRange === 'Year' ? (
                        <DatePicker
                            disableFuture
                            openTo="year"
                            format="yyyy"
                            label="Select Year"
                            views={["year"]}
                            value={dueByDate}
                            onChange={(value) => setPaidOnDate(value)}
                            TextFieldComponent={renderDateInput}
                            margin="normal"
                        />
                    ) : (
                        <DateTimePicker
                            label={dualDates.includes(paidOnRange) ? "Choose Start Date" : "Choose Date"}
                            inputVariant="outlined"
                            value={paidOnDate}
                            onChange={(value) => setPaidOnDate(value)}
                            variant="outlined"
                            TextFieldComponent={renderDateInput}
                            minutesStep={15}
                            margin="normal"
                            clearable
                        />
                    )}
                    <Collapse in={dualDates.includes(paidOnRange)}>
                        <DateTimePicker
                            label="Choose End Date"
                            inputVariant="outlined"
                            value={paidOnDate2}
                            onChange={(value) => setPaidOnDate2(value)}
                            variant="outlined"
                            TextFieldComponent={renderDateInput}
                            minutesStep={15}
                            margin="normal"
                            clearable
                        />
                    </Collapse>
                </div>
            </Collapse>
            <FormGroup className={classes.section}>
                <FormControlLabel
                    control={
                        <Checkbox
                        checked={filters.indexOf('created_at') > -1}
                        onChange={() => handleFilter('created_at')}
                            name="created-at"
                            color="primary"
                        />
                    }
                    label="Date Created"
                />
            </FormGroup>
            <Divider />
            <Collapse in={filters.indexOf('created_at') > -1} classes={{entered: classes.entered}}>
                <div className={classes.subSection}>
                    <TextField
                        select
                        variant="outlined"
                        fullWidth
                        onChange={(e) => setCreatedAtRange(e.target.value)}
                        value={createdAtRange}
                        label="Date Range Type"
                        margin="normal"
                        >
                        {dateRange.map((range, index) => (
                            <MenuItem key={index} value={range}>
                                {range}
                            </MenuItem>
                        ))}
                    </TextField>
                    {createdAtRange === 'Year' ? (
                        <DatePicker
                            disableFuture
                            openTo="year"
                            format="yyyy"
                            label="Select Year"
                            views={["year"]}
                            value={createdAtDate}
                            onChange={(value) => setCreatedAtDate(value)}
                            TextFieldComponent={renderDateInput}
                            margin="normal"
                        />
                    ) : (
                        <DateTimePicker
                            label={dualDates.includes(createdAtRange) ? "Choose Start Date" : "Choose Date"}
                            inputVariant="outlined"
                            value={createdAtDate}
                            onChange={(value) => setCreatedAtDate(value)}
                            variant="outlined"
                            TextFieldComponent={renderDateInput}
                            minutesStep={15}
                            margin="normal"
                        />
                    )}

                    <Collapse in={dualDates.includes(createdAtRange)}>
                        <DateTimePicker
                            label="Choose End Date"
                            inputVariant="outlined"
                            value={createdAtDate2}
                            onChange={(value) => setCreatedAtDate2(value)}
                            variant="outlined"
                            TextFieldComponent={renderDateInput}
                            minutesStep={15}
                            margin="normal"
                        />
                    </Collapse>
                </div>
            </Collapse>
        </Dialog>
    )
}

const useStyles = makeStyles(theme => ({
    container: {
        ...containerFull(theme),
    },
}));

const columns = [
    { id: 'invoice_number', align: 'left', disablePadding: false, label: 'Inv #' },
    {
        id: 'user',
        align: 'left',
        disablePadding: true,
        label: 'User',
        type: 'link',
        link: '/admin/users/',
        link_id: 'user_id'
    },
    { id: 'first', align: 'left', disablePadding: true, label: 'First' },
    { id: 'last', align: 'left', disablePadding: true, label: 'Last' },
    { id: 'email', align: 'left', disablePadding: true, label: 'Email' },
    { id: 'phone_mobile', align: 'left', disablePadding: true, label: 'Phone' },
    { id: 'preferred_address_one', align: 'left', disablePadding: false, label: 'Preferred Address One' },
    { id: 'preferred_address_two', align: 'left', disablePadding: false, label: 'Preferred Address Two' },
    { id: 'preferred_city', align: 'left', disablePadding: false, label: 'Preferred City' },
    { id: 'preferred_state', align: 'left', disablePadding: false, label: 'Preferred State' },
    { id: 'preferred_zip', align: 'left', disablePadding: false, label: 'Preferred Zip' },
    { id: 'type', align: 'left', disablePadding: true, label: 'Type' },
    { id: 'description', align: 'left', disablePadding: true, label: 'Desc' },
    { id: 'reporting_category', align: 'left', disablePadding: true, label: 'Reporting Category' },
    {
        id: 'due_by',
        align: 'left',
        disablePadding: false,
        label: 'Due By',
        type: 'date',
        format: 'D',
    },
    {
        id: 'invoice_by',
        align: 'left',
        disablePadding: false,
        label: 'Invoice Date',
        type: 'date',
        format: 'D'
    },
    {
        id: 'paid_at',
        align: 'left',
        disablePadding: false,
        label: 'Date Paid',
        type: 'date',
        format: 'D'
    },
    {
        id: 'created_at',
        align: 'left',
        disablePadding: false,
        label: 'Date Created',
        type: 'date',
        format: 'D'
    },
    {
        id: 'amount',
        align: 'right',
        disablePadding: false,
        label: 'Amount',
        type: 'currency',
        format: '$'
    },
    { id: 'amount_due', align: 'right', disablePadding: false, label: 'Amount Due', type: 'currency', format: '$'},
    { id: 'amount_paid', align: 'right', disablePadding: false, label: 'Amount Paid', type: 'currency', format: '$'},
    { id: 'amount_refunded', align: 'right', disablePadding: false, label: 'Amount Refunded', type: 'currency', format: '$'},
    { id: 'net_amount', align: 'right', disablePadding: false, label: 'Net Amount', type: 'currency', format: '$'},
    { id: 'method', align: 'left', disablePadding: true, label: 'Method' },
    { id: 'payment_description', align: 'left', disablePadding: true, label: 'Payment Message' },
];

export default function Invoices() {

    const classes = useStyles();

    return (
        <div className={classes.container}>
            <PaginatedTable
                columns={columns}
                stateKey="invoices"
                label="Invoices"
                tableUrl='/invoice'
                baseUrl='/admin/accounting/invoice'
                viewUrl='/admin/accounting/invoices/view'
                newUrl='/admin/accounting/invoices/0'
                exportTitle='invoices-export'
                exportAllTitle='invoices-export-all'
                RefundDialog={<BatchRefundDialog />}
                PaymentDialog={<BatchPaymentDialog type="accounting" />}
                FilterDialog={<InvoiceFilterDialog />}
                EmailDialog={<EmailCampaignsDialog />}
                fullHeight
            />
        </div>
    );
}
